import {Cart, Checkout} from "@connox/typescript-library";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useCheckoutContext} from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import {getShopId} from "../../lib/getShopId";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import {daysStringFunction} from "../DeliveryTime/DeliveryTime";
import styles from "./CartDetails.module.scss";
import {IGetCheckoutShippingOptionsData} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import formatCurrency from "../../../helpers/formatCurrency";

interface ILineProps {
  amount: string;
  text?: string;
  tax?: string;
  showDifferentTaxValues?: boolean;
}

// overview CartDetails component
const CartDetails: React.FC<{reduced?: boolean; selectedShippingOption?: IGetCheckoutShippingOptionsData}> = (
  props,
) => {
  const cartTemp = useCartContext().cart;
  let cart: Cart;
  let checkout: Checkout;
  let isIntoCheckout: boolean;
  try {
    checkout = useCheckoutContext().checkout;
    cart = new Cart(getShopId(), checkout.cartData);
    isIntoCheckout = true;
  } catch (err) {
    cart = cartTemp;
    isIntoCheckout = false;
  }
  const {t} = useTranslation("cart");
  const shippingPositions = cart.positions.filter((pos) => pos.type === "Shipping");
  const discountPositions = cart.positions.filter(
    (pos) => (!pos.reference || pos.reference.itemId === null) && pos.type !== "Shipping",
  );

  // check if voucherType is Relative or not
  const isVoucherRelative = !!cart.positions.filter((pos) => pos.reference?.voucher?.type === "Relative").length;

  // check if it exists 2 oder more than 2 voucher positions
  const isMoreThanOneRow = (): boolean => {
    // filter all items with type voucher
    const voucherPosition = cart.positions.filter((pos) => pos.type === "Voucher");

    return voucherPosition.length >= 2;
  };

  const hasRebate = cart.valueRebate && parseInt(cart.valueRebate.amount, 10) > 0;

  //  detailLine component
  const DetailLine: React.FC<ILineProps> = (dlProps) => (
    <div className={styles.detailLine}>
      <span>
        {dlProps.text}
        {/*/ show mwst beside relative voucher when this conditions are true */}
        {dlProps.showDifferentTaxValues && isVoucherRelative && isMoreThanOneRow() ? (
          <span className={styles.taxPadding}>{t("({{taxPercent}} MwSt.)", {taxPercent: dlProps.tax})}</span>
        ) : null}
      </span>
      <span>{dlProps.amount === t("gratis") ? <strong>{dlProps.amount}</strong> : dlProps.amount}</span>
    </div>
  );

  //  strongLine component
  const StrongLine: React.FC<ILineProps> = (slProps) => (
    <div className={styles.strongLine}>
      <span>
        <strong>{slProps.text}</strong>{" "}
        {cart.isNet ? (cart.countryCode === "GB" ? "*" : t("exkl. MwSt.")) : t("inkl. MwSt.")}
      </span>
      <span>
        <strong>{slProps.amount}</strong>
      </span>
    </div>
  );

  // check if Discounts exists
  const Discounts: React.FC = () => {
    if (!discountPositions) {
      return null;
    }

    return (
      <React.Fragment>
        {discountPositions.map((pos, key) => {
          return (
            <DetailLine
              key={key}
              showDifferentTaxValues={true}
              text={pos.text}
              tax={pos.tax.percent.formatted}
              amount={pos.amount.formatted}
            />
          );
        })}
      </React.Fragment>
    );
  };

  const Taxes: React.FC = () => {
    return (
      <React.Fragment>
        <DetailLine amount={cart.amountNet.formatted} text={t("Nettobetrag")} />
        {cart.taxAmounts.map((tax, key) => {
          return (
            <DetailLine
              showDifferentTaxValues={false}
              key={key}
              text={t("MwSt. ({{taxPercent}})", {taxPercent: tax.percent.formatted})}
              amount={tax.amount.formatted}
            />
          );
        })}
      </React.Fragment>
    );
  };

  const Shipping: React.FC = () => {
    if (props.selectedShippingOption) {
      const {amount, deliveryDaysMin, deliveryDaysMax, name} = props.selectedShippingOption;
      const selectedShippingOption = Number(amount.amount);
      const shippingCost = cart.isNet ? selectedShippingOption / 1.19 : selectedShippingOption;
      const shippingAmount = amount.amount === "0.00" ? t("gratis") : formatCurrency(shippingCost, cart.currencyCode);
      const minMaxSet = deliveryDaysMin && deliveryDaysMax;
      const deliveryString: string = minMaxSet ? ` (${daysStringFunction(deliveryDaysMin, deliveryDaysMax, t)})` : "";

      return (
        <div className={styles.shipping}>
          <span>{`${name}${deliveryString}`}</span>
          <span>{shippingAmount}</span>
        </div>
      );
    }

    return (
      <React.Fragment>
        {shippingPositions.map((pos, key) => {
          if (pos.messages.some((msg) => msg.type === "error")) {
            return null;
          }
          const amount = pos.amount.amount === "0.00" ? t("gratis") : pos.amount.formatted;
          const {deliveryDaysMin, deliveryDaysMax} = cart.shippingInformation;
          const minMaxSet = deliveryDaysMin && deliveryDaysMax;
          const deliveryString: string = minMaxSet
            ? ` (${daysStringFunction(deliveryDaysMin, deliveryDaysMax, t)})`
            : "";

          return (
            <div key={key} className={styles.shipping}>
              <span>{`${pos.text}${deliveryString}`}</span>
              <span>{amount}</span>
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  const DetailsListReduced: React.FC = () => {
    const payable = Number(cart.valuePayable.amount);
    const oldShipping = Number(shippingPositions[0]?.amount.amount || "0.00");
    const selectedShippingAmount = Number(props.selectedShippingOption?.amount.amount || "0.00");
    const newShipping = cart.isNet ? selectedShippingAmount / 1.19 : selectedShippingAmount;

    // recalucate the cost considering the discount skonto 3% when the payment method is vorkasse.
    const calculatedAmount =
      payable -
      oldShipping +
      (checkout.paymentId === 100013 ? oldShipping * 0.03 : 0) +
      newShipping -
      (checkout.paymentId === 100013 ? newShipping * 0.03 : 0);
    const finalCost = props.selectedShippingOption
      ? formatCurrency(calculatedAmount, cart.currencyCode)
      : cart.valuePayable.formatted;

    return (
      <React.Fragment>
        <Shipping />
        <StrongLine amount={finalCost} text={t("Summe")} />
      </React.Fragment>
    );
  };

  const CustomsNotification: React.FC = () => (
    <React.Fragment>
      <div className={`${styles.customsNotificationGb} ${styles.svgIconCautionOutline}`}>
        We ship from Germany. Orders exceeding 134 GBP will incur customs/import fees/taxes.
      </div>
    </React.Fragment>
  );

  const StrikeDateNotification: React.FC = () => (
    <React.Fragment>
      <div className={`${styles.customsNotificationGb} ${styles.svgIconCautionOutline}`}>
        <b>CWU Strike Date Notification - Delivery delays are expected</b>
        <br /> Please be advised, if your order is being delivered within the UK you may experience some delays. This is
        due to the{" "}
        <a
          href="https://www.parcelforce.com/latest-news"
          target="_blank"
          rel="noreferrer"
          title="More information about the CWU Strike"
        >
          CWU strike action
        </a>{" "}
        scheduled to take place in the UK over the next coming months. We apologise for any inconvenience this may cause
        to your delivery.
      </div>
    </React.Fragment>
  );

  const DetailsList: React.FC = () => (
    <React.Fragment>
      <StrongLine text={t("Zwischensumme")} amount={cart.valueSubtotal.formatted} />
      {cart.isCorporateCustomer && <Taxes />}
      <Discounts />
      <Shipping />
      <hr />
      <StrongLine text={t("Summe")} amount={cart.amountGross.formatted} />
      {hasRebate && cart.valueRebate && (
        <React.Fragment>
          <DetailLine text={t("Gutschein")} amount={cart.valueRebate.formatted} />
          <StrongLine text={t("Zu zahlender Betrag")} amount={cart.valuePayable.formatted} />
        </React.Fragment>
      )}

      {cart.countryCode === "GB" && parseFloat(cart.valueSubtotal.amount) > 134 ? <CustomsNotification /> : null}
      {cart.countryCode === "GB" && <StrikeDateNotification />}
    </React.Fragment>
  );

  return (
    // class 'CartDetails' is still in use by ShoppingCart to
    // alternatively set bg color to white in desktop view
    <div className={`${styles.wrapper} ${isIntoCheckout ? styles.checkout : ""}`}>
      {props.reduced ? <DetailsListReduced /> : <DetailsList />}
    </div>
  );
};

export default CartDetails;
